import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { Button, Dropdown, Form, Table } from 'react-bootstrap';
import { Check2, CpuFill, Journals, PencilSquare, Trash3 } from 'react-bootstrap-icons';
import { useLoaderData, useLocation, useOutletContext } from 'react-router-dom';

import { CreateWOLink } from '@/components/createworkorderlink';
import { ListLink } from '@/components/listlink';
import { Pager } from '@/components/pager';
import { SearchReset } from '@/components/search-reset';
import { Sorter } from '@/components/sorter';
import { getDateList, getList } from '@/helper/apicalls';
import { Holiday, Pta, Session, SessionListResponse } from '@/helper/interfaces';
import { formatStatus, getColorClass } from '@/helper/utility';
import { useStore } from '@/store';

import { baseURL, settings } from './const';

export async function listLoader({ request }: any) {
  const parameters = new URL(request.url).searchParams;
  return getDateList(baseURL, parameters, settings);
}

export async function searchLoader({ request }: any) {
  const parameters = new URL(request.url).searchParams;
  return getList(baseURL + '/search', parameters, settings);
}

interface Modals {
  editSession: SessionFunction;
  deleteSession: SessionFunction;
  showNotes: SessionFunction;
  showWorkOrderInfo: SessionFunction;
  editPta: PtaFunction;
  deletePta: PtaFunction;
  editHoliday: HolidayFunction;
  deleteHoliday: HolidayFunction;
  setExcludedUsersLoader: React.Dispatch<React.SetStateAction<number[]>>;
}
interface SessionFunction {
  (session: Session): void;
}
interface PtaFunction {
  (pta: Pta): void;
}
interface HolidayFunction {
  (holiday: Holiday): void;
}

interface SessionRowProperties {
  session: Session;
  isSearch: boolean;
  canBulkSelect: boolean;
  canViewPrivateNotes: boolean;
  canEditSessions: boolean;
  selectedSessions: number[];
  disableModals: boolean;
  handleSelect: (event: React.ChangeEvent<HTMLInputElement>, id: number) => void;
  showNotes: SessionFunction;
  showWorkOrderInfo: SessionFunction;
  editSession: SessionFunction;
  deleteSession: SessionFunction;
  setDisableModals: React.Dispatch<React.SetStateAction<boolean>>;
}

function hasText(value?: string | null) {
  return Boolean(value && value.length > 0);
}

function hasVisibleNotes(session: Session, canViewPrivateNotes: boolean) {
  return (
    hasText(session.notes) ||
    hasText(session.company?.notes) ||
    hasText(session.contact?.notes) ||
    hasText(session.project?.notes) ||
    (canViewPrivateNotes && hasText(session.project?.privateNotes)) ||
    hasText(session.workOrder?.notes)
  );
}

function getNotesButtonClassName(session: Session, notesVisible: boolean) {
  return 'btn-icon' + (hasText(session.notes) ? ' highlight' : '') + (notesVisible ? '' : ' text-secondary');
}

function getWorkOrderInfoTitle(hasWorkOrder: boolean) {
  return hasWorkOrder ? 'Titles and Technical Data' : 'Work order does not exist';
}

function getWorkOrderButtonClassName(hasWorkOrder: boolean) {
  return 'btn-icon' + (hasWorkOrder ? '' : ' text-secondary');
}

function formatSessionStartTime(session: Session, isSearch: boolean) {
  return format(parseISO(session.scheduleStartTime ?? session.startTime), isSearch ? 'MM/dd/yy h:mm a' : 'h:mm a');
}

function formatSessionEndTime(session: Session) {
  return format(parseISO(session.scheduleEndTime ?? session.endTime), 'h:mm a');
}

function ScheduleRowActions({
  session,
  canViewPrivateNotes,
  canEditSessions,
  disableModals,
  showNotes,
  showWorkOrderInfo,
  editSession,
  deleteSession,
  setDisableModals
}: Pick<
  SessionRowProperties,
  | 'session'
  | 'canViewPrivateNotes'
  | 'canEditSessions'
  | 'disableModals'
  | 'showNotes'
  | 'showWorkOrderInfo'
  | 'editSession'
  | 'deleteSession'
  | 'setDisableModals'
>) {
  const hasWorkOrder = Boolean(session.workOrder?.id);
  const notesVisible = hasVisibleNotes(session, canViewPrivateNotes);
  const workOrderInfoTitle = getWorkOrderInfoTitle(hasWorkOrder);

  return (
    <div className='schedule-row-actions'>
      <Button
        variant='link'
        className={getNotesButtonClassName(session, notesVisible)}
        onClick={() => showNotes(session)}
        disabled={disableModals || !notesVisible}
      >
        <Journals title='Show Notes' aria-label='show notes' />
      </Button>
      <CreateWOLink sessionId={session.id} disableModals={disableModals} setDisableModals={setDisableModals} />
      <span title={workOrderInfoTitle} className='d-inline-block'>
        <Button
          variant='link'
          className={getWorkOrderButtonClassName(hasWorkOrder)}
          onClick={() => showWorkOrderInfo(session)}
          disabled={!hasWorkOrder || disableModals}
          aria-label='titles and technical data'
        >
          <CpuFill title={workOrderInfoTitle} />
        </Button>
      </span>
      {canEditSessions && (
        <React.Fragment>
          <Button variant='link' className='btn-icon' onClick={() => editSession(session)} disabled={disableModals}>
            <PencilSquare title='Edit Session' aria-label='edit' />
          </Button>
          <Button variant='link' className='btn-icon' onClick={() => deleteSession(session)} disabled={disableModals}>
            <Trash3 title='Delete Session' aria-label='delete' />
          </Button>
        </React.Fragment>
      )}
    </div>
  );
}

function SessionRow({
  session,
  isSearch,
  canBulkSelect,
  canViewPrivateNotes,
  canEditSessions,
  selectedSessions,
  disableModals,
  handleSelect,
  showNotes,
  showWorkOrderInfo,
  editSession,
  deleteSession,
  setDisableModals
}: SessionRowProperties) {
  const status = formatStatus(session.status, session?.workOrder?.isFinished);

  return (
    <tr className={getColorClass(status)}>
      {canBulkSelect && (
        <td>
          <Form.Check
            id={'SES' + session.id}
            checked={selectedSessions?.includes(session.id)}
            onChange={(event) => handleSelect(event, session.id)}
          />
        </td>
      )}
      <ListLink to={`/companies/${session.company.id}`}>{session.company.name}</ListLink>
      <ListLink to={`/projects/${session.project.id}`}>
        <b>{session.project.name}</b>
      </ListLink>
      <ListLink to={`/contacts/${session.contact.id}`}>
        {session.contact.firstName} {session.contact.lastName}
      </ListLink>
      <td>{formatSessionStartTime(session, isSearch)}</td>
      <td>{formatSessionEndTime(session)}</td>
      <td>{session.unsupervised ? <Check2 /> : ''}</td>
      <td>{session.mixer.name}</td>
      <td>{session.assistants.map((assistant) => assistant.name).join(', ')}</td>
      <td>
        <b>{session.room.name}</b>
      </td>
      <td>{status}</td>
      <td className='text-center'>
        <ScheduleRowActions
          session={session}
          canViewPrivateNotes={canViewPrivateNotes}
          canEditSessions={canEditSessions}
          disableModals={disableModals}
          showNotes={showNotes}
          showWorkOrderInfo={showWorkOrderInfo}
          editSession={editSession}
          deleteSession={deleteSession}
          setDisableModals={setDisableModals}
        />
      </td>
    </tr>
  );
}

export function List() {
  const { user, selectedSessions, setSelectedSessions } = useStore();
  const location = useLocation();
  const isSearch = location.pathname.replace(/\/+$/, '') == '/schedule/search';
  const canBulkSelect = isSearch && Boolean(user?.role?.editSessions);
  const canViewPrivateNotes = Boolean(user?.role?.privateNotes);

  const { data, currentPage, numberOfPages, orderBy, order } = useLoaderData() as SessionListResponse;
  const {
    editSession,
    deleteSession,
    showNotes,
    showWorkOrderInfo,
    editPta,
    deletePta,
    editHoliday,
    deleteHoliday,
    setExcludedUsersLoader
  } = useOutletContext<Modals>();

  const [disableModals, setDisableModals] = React.useState(false);
  const columnCount = canBulkSelect ? 12 : 11;
  const allSessions = data?.sessions?.map((session) => session.id) || [];

  function handleSelect(event: React.ChangeEvent<HTMLInputElement>, id: number) {
    const updated = event.target.checked
      ? [...selectedSessions, id]
      : selectedSessions.filter((sessionId: number) => sessionId !== id);
    setSelectedSessions(updated);
  }

  function selectAll(event: React.ChangeEvent<HTMLInputElement>) {
    const updated = event.target.checked
      ? [...selectedSessions, ...allSessions]
      : selectedSessions.filter((sessionId: number) => !allSessions.includes(sessionId));
    setSelectedSessions(updated);
  }

  React.useEffect(() => {
    setExcludedUsersLoader(data?.pta?.map((p) => p.userId));
  }, [data?.pta]);

  return (
    <React.Fragment>
      <Table striped bordered hover size='sm'>
        <thead>
          {data?.pta?.length > 0 && (
            <tr>
              <td colSpan={columnCount}>
                <b>PTA:</b>
                {data?.pta?.map((p) => (
                  <Dropdown key={p.id} as='span' drop='end' className='ms-2 me-2'>
                    {p.user.name}
                    <Dropdown.Toggle
                      split
                      variant='link'
                      className='ms-2 align-baseline'
                      disabled={!user?.role?.editSessions}
                    />
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => editPta(p)}>
                        <PencilSquare className='align-text-top' /> Edit
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => deletePta(p)}>
                        <Trash3 className='align-text-top' /> Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ))}
              </td>
            </tr>
          )}
          <tr>
            {canBulkSelect && (
              <th>
                <Form.Check
                  id='selectAllSessions'
                  checked={allSessions?.length > 0 ? allSessions.every((id) => selectedSessions.includes(id)) : false}
                  onChange={(event) => selectAll(event)}
                />
              </th>
            )}
            <Sorter name='company' orderBy={orderBy} order={order} />
            <Sorter name='project' orderBy={orderBy} order={order} />
            <Sorter name='contact' orderBy={orderBy} order={order} />
            <Sorter name='startTime' orderBy={orderBy} order={order} />
            <Sorter name='endTime' orderBy={orderBy} order={order} />
            <Sorter name='unsupervised' orderBy={orderBy} order={order}>
              US
            </Sorter>
            <Sorter name='mixer' orderBy={orderBy} order={order} />
            <th>Assistant</th>
            <Sorter name='room' orderBy={orderBy} order={order} />
            <Sorter name='status' orderBy={orderBy} order={order} />
            <td></td>
          </tr>
        </thead>
        <tbody>
          {data?.holiday && (
            <tr className='red'>
              <td colSpan={columnCount}>
                Lime Closed
                {user?.role?.users && (
                  <React.Fragment>
                    <Button
                      variant='link'
                      className='btn-icon ms-2'
                      onClick={() => editHoliday(data?.holiday)}
                      disabled={disableModals}
                    >
                      <PencilSquare className='align-text-top' title='Edit' />
                    </Button>
                    <Button
                      variant='link'
                      className='btn-icon'
                      onClick={() => deleteHoliday(data?.holiday)}
                      disabled={disableModals}
                    >
                      <Trash3 className='align-text-top' title='Delete' />
                    </Button>
                  </React.Fragment>
                )}
              </td>
            </tr>
          )}
          {data?.sessions?.length > 0 ? (
            data?.sessions?.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                isSearch={isSearch}
                canBulkSelect={canBulkSelect}
                canViewPrivateNotes={canViewPrivateNotes}
                canEditSessions={Boolean(user?.role?.editSessions)}
                selectedSessions={selectedSessions}
                disableModals={disableModals}
                handleSelect={handleSelect}
                showNotes={showNotes}
                showWorkOrderInfo={showWorkOrderInfo}
                editSession={editSession}
                deleteSession={deleteSession}
                setDisableModals={setDisableModals}
              />
            ))
          ) : (
            <tr>
              <td colSpan={columnCount}>No Sessions</td>
            </tr>
          )}
        </tbody>
      </Table>

      {isSearch && (
        <React.Fragment>
          <SearchReset onReset={() => setSelectedSessions([])} />
          <Pager currentPage={currentPage} numberOfPages={numberOfPages} />
        </React.Fragment>
      )}
    </React.Fragment>
  );
}
